'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { CartItemFragment } from '@/fo/api/magento';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitListCharacteristic } from '@/fo/ui/kit/list/characteristic';
import { UiKitPriceBase } from '@/fo/ui/kit/price/base';
import { UiPaymentBnplBadge } from '@/fo/ui/payment/bnpl/badge';

import { CheckoutItemsModal } from '../components/CheckoutItemsModal/CheckoutItemsModal';
import { Promo, PromoProps } from '../components/Promo/Promo';

import { styles } from './UiOrderSummaryCompact.styles';

export interface OrderSummaryItems {
  label: React.ReactNode;
  value: React.ReactNode;
}

export type SlotNameProps = 'top' | 'bottom' | 'bnplBadge';

/** Поля product из GraphQL, не раскрытые типом фрагмента CartItem */
interface ProductDisplay {
  image?: { src?: string } | null;
  name?: string | null;
}

export interface UiOrderSummaryCompactProps extends PromoProps {
  onClick?: () => void;
  items: OrderSummaryItems[];
  totalPrice: number;
  subtotalPrice: number;
  buttonText: string;
  className?: string;
  products?: CartItemFragment[];
  loading?: boolean;
  disabled?: boolean;
  slots?: Partial<Record<SlotNameProps, React.ReactNode>>;
  isAuthorized?: boolean;
  allowPromo?: boolean;
  allowButton?: boolean;
  allowProducts?: boolean;
  currency?: string;
  orderTitle?: string;
  variant?: 'checkout' | 'cartDrawer';
  mainButtonRef?: React.Ref<HTMLButtonElement>;
}

export function UiOrderSummaryCompact({
  className,
  items,
  totalPrice,
  subtotalPrice,
  buttonText,
  products,
  loading = false,
  disabled = false,
  onClick,
  slots: slotsMap = {},
  isAuthorized,
  allowPromo = true,
  allowButton = true,
  allowProducts = false,
  currency,
  orderTitle,
  variant = 'cartDrawer',
  mainButtonRef,
  canSpendBonuses,
  hasSpendBonuses,
  ...promoProps
}: UiOrderSummaryCompactProps) {
  const slots = styles({ variant });
  const t = useTranslations();
  const [mappedProducts, productsLeftCount] =
    products && Boolean(products.length)
      ? [products.slice(0, 3), products.length - 3]
      : [null, 0];

  const [isOrderDetailModalOpen, setIsOrderDetailModalOpen] = useState(false);
  const handleClickOrderDetail = () => {
    setIsOrderDetailModalOpen(!isOrderDetailModalOpen);
  };

  return (
    <>
      <div className={slots.base({ className })}>
        {allowPromo && (
          <>
            {!canSpendBonuses && (
              <div className={slots.header()}>
                <h2 className={slots.title()}>{t('promoCode')}</h2>
              </div>
            )}

            <Promo
              {...promoProps}
              subtotalPrice={subtotalPrice}
              isAuthorized={isAuthorized}
              canSpendBonuses={canSpendBonuses}
              hasSpendBonuses={hasSpendBonuses}
            />
          </>
        )}

        <div className={slots.headerAmount()}>
          <h2 className={slots.title()}>{orderTitle || t('Order amount')}</h2>
        </div>

        {allowProducts && (
          <div className={slots.productWrapper()}>
            {mappedProducts &&
              mappedProducts.map((item) => {
                const product = item.product as
                  | ProductDisplay
                  | null
                  | undefined;

                return (
                  <div
                    className={slots.productItem()}
                    key={item.id}
                    onClick={() => handleClickOrderDetail()}
                  >
                    <Image
                      className={slots.productImage()}
                      width={96}
                      height={96}
                      src={product?.image?.src ?? ''}
                      alt={product?.name ?? ''}
                      itemProp="image"
                    />
                    <div className={slots.productCount()}>{item.quantity}</div>
                  </div>
                );
              })}

            {productsLeftCount > 0 && (
              <div className={slots.productLeftCount()}>
                +{productsLeftCount}
              </div>
            )}
          </div>
        )}

        <div className={slots.totals()}>
          <UiKitListCharacteristic
            items={items}
            className={slots.characteristic()}
            classNameLabel={slots.characteristicLabel()}
            classNameValue={slots.characteristicValue()}
            classNameItem={slots.characteristicItem()}
          />

          <div className={slots.grandTotal()}>
            <span className={slots.grandTotalLabel()}>{t('total')}</span>

            <UiKitPriceBase
              value={totalPrice}
              className={slots.grandTotalPrice()}
              classNameValue="heading-xl lg:heading-2xl !font-[450]"
              currency={currency}
            />
          </div>

          {slotsMap.bnplBadge !== undefined ? (
            slotsMap.bnplBadge
          ) : (
            <UiPaymentBnplBadge
              className={slots.yaSplitBadge()}
              amount={totalPrice}
              size="m"
              variant="detailed"
              color="transparent"
              theme="light"
              align="left"
            />
          )}
        </div>

        {allowButton && (
          <UiKitButtonBase
            ref={mainButtonRef}
            color="secondary"
            className={slots.button()}
            loading={loading}
            disabled={disabled || loading}
            onClick={onClick}
            id="order-summary-button"
          >
            {buttonText}
          </UiKitButtonBase>
        )}

        {slotsMap.bottom}
      </div>

      {allowProducts && (
        <CheckoutItemsModal
          isOpen={isOrderDetailModalOpen}
          setIsOpen={setIsOrderDetailModalOpen}
          products={products ?? []}
        />
      )}
    </>
  );
}
